import React from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowRight, Gem, TrendingUp, Users, Globe, Heart, Shield } from 'lucide-react';

const InvestorLanding = () => {
  const openWhatsApp = () => {
    window.open('https://wa.me/919871155963?text=Hi!%20I%27m%20interested%20in%20learning%20more%20about%20investing%20in%20GoHimalayan.', '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5">
              <Mountain className="h-7 w-7 text-[#2C5F8D]" strokeWidth={2.5} />
              <span className="text-lg font-bold tracking-tight text-gray-900" style={{fontFamily: 'Playfair Display, serif'}}>
                GoHimalayan
              </span>
            </Link>
            
            <Link 
              to="/"
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-40 pb-16 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C5F8D]/10 rounded-full mb-8">
            <Gem className="w-4 h-4 text-[#2C5F8D]" />
            <span className="text-sm font-medium text-[#2C5F8D]">For Investors</span>
          </div>
          
          <div className="relative mb-12">
            {/* Background 100 */}
            <span 
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[60%] text-[12rem] sm:text-[16rem] font-bold text-gray-200 italic select-none pointer-events-none"
              style={{fontFamily: 'Playfair Display, serif'}}
            >
              100
            </span>
            
            {/* Main Title */}
            <h1 
              className="relative text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] tracking-tight z-10"
              style={{fontFamily: 'Playfair Display, serif'}}
            >
              The Investment Circle
            </h1>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="pb-16 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-lg text-gray-600 leading-relaxed space-y-6">
            <p>
              GoHimalayan is more than a travel platform. It is an endeavour to shape how the Himalayas are experienced — thoughtfully, sustainably, and with enduring purpose.
            </p>
            <p>
              We are building a collective of 100 distinctive stays across the Indian Himalayas. A portfolio of places shaped by landscape, legacy, and personal vision — not by algorithms or mass appeal.
            </p>
            <p className="font-medium text-gray-800">
              For those who share this vision, we offer an opportunity to participate in its growth.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="flex items-center justify-center pb-16">
        <div className="h-px w-16 bg-gray-200"></div>
        <Mountain className="w-6 h-6 text-[#2C5F8D] mx-4" />
        <div className="h-px w-16 bg-gray-200"></div>
      </div>

      {/* The Opportunity */}
      <section className="pb-16 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 
            className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center"
            style={{fontFamily: 'Playfair Display, serif'}}
          >
            The Opportunity
          </h2>
          
          <div className="prose prose-lg text-gray-600 leading-relaxed space-y-6">
            <p>
              Experiential travel in the Himalayas remains underserved by platforms that understand its nuance. The region's finest stays — independent, design-led, deeply personal — often lack the visibility and infrastructure they deserve.
            </p>
            <p>
              GoHimalayan addresses this gap with a curated approach: selective partnerships, refined storytelling, centralised booking infrastructure, and access to a discerning traveller community.
            </p>
            <p>
              Our model is intentionally constrained. By limiting the collective to 100 stays, we preserve quality, deepen relationships, and build lasting regional authority.
            </p>
          </div>
        </div>
      </section>

      {/* What We Are Building */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 
            className="text-2xl sm:text-3xl font-bold text-gray-900 mb-10 text-center"
            style={{fontFamily: 'Playfair Display, serif'}}
          >
            What We Are Building
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-gray-50">
              <div className="w-10 h-10 rounded-full bg-[#2C5F8D]/10 flex items-center justify-center mb-4">
                <Globe className="w-5 h-5 text-[#2C5F8D]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Regional Authority</h3>
              <p className="text-gray-600 text-sm">
                The definitive platform for considered Himalayan hospitality — trusted by travellers and hosts alike.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-gray-50">
              <div className="w-10 h-10 rounded-full bg-[#2C5F8D]/10 flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-[#2C5F8D]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Host Collective</h3>
              <p className="text-gray-600 text-sm">
                100 independent stays, interconnected yet distinct, sharing infrastructure while retaining individuality.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-gray-50">
              <div className="w-10 h-10 rounded-full bg-[#2C5F8D]/10 flex items-center justify-center mb-4">
                <Heart className="w-5 h-5 text-[#2C5F8D]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Traveller Community</h3>
              <p className="text-gray-600 text-sm">
                A growing circle of discerning guests seeking depth, authenticity, and meaningful journeys.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-gray-50">
              <div className="w-10 h-10 rounded-full bg-[#2C5F8D]/10 flex items-center justify-center mb-4">
                <TrendingUp className="w-5 h-5 text-[#2C5F8D]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Sustainable Growth</h3>
              <p className="text-gray-600 text-sm">
                Patient capital, thoughtful expansion, and a commitment to long-term value over short-term scale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Whom */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 
            className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center"
            style={{fontFamily: 'Playfair Display, serif'}}
          >
            Who This Is For
          </h2>
          
          <div className="prose prose-lg text-gray-600 leading-relaxed text-center mb-10">
            <p>
              We welcome conversations with individuals and organisations who resonate with our approach — those who see value in patience, curation, and building something of lasting significance.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            <div className="text-center p-8 rounded-2xl border border-gray-200 bg-white">
              <div className="w-12 h-12 rounded-full bg-[#2C5F8D]/10 flex items-center justify-center mx-auto mb-4">
                <Gem className="w-6 h-6 text-[#2C5F8D]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2" style={{fontFamily: 'Playfair Display, serif'}}>
                Individual Investors
              </h3>
              <p className="text-gray-600 text-sm">
                Those seeking meaningful participation in an alternative asset class rooted in place and purpose.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl border border-gray-200 bg-white">
              <div className="w-12 h-12 rounded-full bg-[#2C5F8D]/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-[#2C5F8D]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2" style={{fontFamily: 'Playfair Display, serif'}}>
                Strategic Partners
              </h3>
              <p className="text-gray-600 text-sm">
                Organisations aligned with experiential hospitality, sustainable tourism, or Himalayan development.
              </p>
            </div>
          </div>

          <p className="text-gray-500 text-center mt-10 text-sm italic">
            This is not a public offering. We engage selectively, through relationship and alignment.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8 bg-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <h2 
            className="text-2xl sm:text-3xl font-bold text-white mb-4"
            style={{fontFamily: 'Playfair Display, serif'}}
          >
            A Quiet Invitation
          </h2>
          <p className="text-gray-400 mb-8 max-w-lg mx-auto">
            If our vision resonates, we would welcome a conversation — unhurried, exploratory, and without obligation.
          </p>
          
          <button 
            onClick={openWhatsApp}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-gray-900 rounded-full font-semibold hover:bg-gray-100 transition-all"
            data-testid="investor-connect-cta"
          >
            Express Interest
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 lg:px-8 border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Mountain className="h-5 w-5 text-[#2C5F8D]" />
            <span className="text-sm text-gray-500">© 2026 GoHimalayan</span>
          </div>
          <Link 
            to="/"
            className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default InvestorLanding;
